"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { AdvertoryTargetId } from "../Advertory/types";
import type { CardsResponse } from "../../types/content";
import type { FeedItems } from "../../types/content/feedItem";
import type { InjectableSnapshot } from "../../types/injectables";
import type { ListView } from "../../types/webTypes";
import type { SponsoredBannerData } from "../../types/ads/sponsoredBanner";

import { fetchFeedFromUrl } from "../../services/fetchFeed";
import { addInjectablesToFeed } from "../../utils/addInjecteablesToFeed";
import { DotLoader } from "../DotLoader/DotLoader";
import { Feed } from "../Feed/Feed";
import styles from "./FeedLoader.module.scss";
import { omitRepeated } from "./util/omitRepeated";
import { useFeedPaginationObserver } from "./useFeedPaginationObserver";

const hasInjectableSlots = (injectables?: InjectableSnapshot | null) =>
  Array.isArray(injectables?.slots) && injectables.slots.length > 0;

const shouldRenderNoMoreArticlesMarker = (
  initialItemsCount: number,
  loadedItemsCount: number,
) => initialItemsCount > 0 || loadedItemsCount > 0;

const FeedLoader = ({
  children,
  injectables,
  fetchFeedUrl,
  deferInitialLoadUntilScroll = false,
  advertoryWidescreenTargetId,
  sponsoredBannerData,
  feedLayoutType,
  initialItems,
  listView,
  headerTitle,
  cards,
  isLoggedIn,
}: React.PropsWithChildren<{
  children?: React.ReactNode;
  cards?: CardsResponse;
  injectables?: InjectableSnapshot | null;
  fetchFeedUrl: string;
  deferInitialLoadUntilScroll?: boolean;
  advertoryWidescreenTargetId?: Extract<
    AdvertoryTargetId,
    "widescreen_1" | "widescreen_2"
  >;
  sponsoredBannerData?: SponsoredBannerData | null;
  feedLayoutType: "top" | "latest" | "list";
  listView?: ListView;
  initialItems: FeedItems;
  headerTitle?: string;
  isLoggedIn: boolean;
}>) => {
  const feedDataRef = useRef<FeedItems>([]);
  const [feedData, setFeedData] = useState<FeedItems>([]);
  const [loading, setLoading] = useState(false);
  const [fetchUrl, setFetchUrl] = useState(fetchFeedUrl);
  const [noMoreArticles, setNoMoreArticles] = useState(false);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const [injectablesSnapshot, setInjectablesSnapshot] = useState(injectables);
  const injectablesSnapshotRef = useRef(injectables);
  const shouldPreloadNextFeedPage = hasInjectableSlots(injectablesSnapshot);

  useEffect(() => {
    feedDataRef.current = feedData;
  }, [feedData]);

  useEffect(() => {
    injectablesSnapshotRef.current = injectablesSnapshot;
  }, [injectablesSnapshot]);

  useEffect(() => {
    if (!deferInitialLoadUntilScroll || hasUserScrolled) {
      return;
    }

    const enableInitialLoad = () => setHasUserScrolled(true);

    window.addEventListener("scroll", enableInitialLoad, {
      once: true,
      passive: true,
    });

    return () => window.removeEventListener("scroll", enableInitialLoad);
  }, [deferInitialLoadUntilScroll, hasUserScrolled]);

  const loadMoreFeedData = useCallback(
    async (url: string) => {
      setLoading(true);
      try {
        const fetchedFeedData = await fetchFeedFromUrl({
          path: url,
          isLoggedIn,
        });
        if (!fetchedFeedData?.data || !fetchedFeedData.data.articles) {
          setFetchUrl("");
          return;
        }

        if (
          !fetchedFeedData.error &&
          fetchedFeedData.data.articles.length === 0
        ) {
          setFetchUrl("");
          if (
            shouldRenderNoMoreArticlesMarker(
              initialItems.length,
              feedDataRef.current.length,
            )
          ) {
            setNoMoreArticles(true);
          }
          return;
        }

        const currentFeedData = feedDataRef.current;
        const currentInjectablesSnapshot = injectablesSnapshotRef.current;
        let appendedFeedData = fetchedFeedData.data.articles;
        let nextInjectablesSnapshot = currentInjectablesSnapshot;
        const hasInjectableConfigPositions = hasInjectableSlots(
          currentInjectablesSnapshot,
        );

        const filteredData = omitRepeated(
          currentFeedData,
          appendedFeedData,
          initialItems,
          true,
        );

        if (hasInjectableConfigPositions && currentInjectablesSnapshot) {
          [appendedFeedData, nextInjectablesSnapshot] = addInjectablesToFeed(
            filteredData,
            currentInjectablesSnapshot,
          );
        } else {
          appendedFeedData = filteredData;
        }

        const nextFeedData = [...currentFeedData, ...appendedFeedData];

        feedDataRef.current = nextFeedData;
        injectablesSnapshotRef.current = nextInjectablesSnapshot;

        setFeedData(nextFeedData);
        setFetchUrl(fetchedFeedData.data.links?.next || "");
        setInjectablesSnapshot(nextInjectablesSnapshot);
      } finally {
        setLoading(false);
      }
    },
    [initialItems, isLoggedIn],
  );

  const paginationRef = useFeedPaginationObserver({
    fetchUrl: deferInitialLoadUntilScroll && !hasUserScrolled ? "" : fetchUrl,
    isLoading: loading,
    loadMoreFeedData,
    shouldPreloadNextFeedPage,
  });

  if (!fetchFeedUrl) {
    if (feedLayoutType !== "list" || initialItems.length === 0) {
      return <>{children ? children : null}</>;
    }

    return (
      <Feed
        data={initialItems}
        advertoryWidescreenTargetId={advertoryWidescreenTargetId}
        sponsoredBannerData={sponsoredBannerData}
        layoutType="list"
        listView={listView}
        isLoggedIn={isLoggedIn}
        isFirstFeed
        headerTitle={headerTitle || ""}
      />
    );
  }

  return (
    <div className={styles.feedLoaderContainer}>
      {feedLayoutType === "list" ? (
        <Feed
          data={[...initialItems, ...feedData]}
          advertoryWidescreenTargetId={advertoryWidescreenTargetId}
          sponsoredBannerData={sponsoredBannerData}
          layoutType={feedLayoutType}
          listView={listView}
          cards={cards}
          isLoggedIn={isLoggedIn}
          isFirstFeed={Array.isArray(initialItems) && initialItems.length > 0}
          endOfFeed={noMoreArticles}
          headerTitle={headerTitle || ""}
        />
      ) : (
        <>
          {children}
          {feedData && (
            <Feed
              data={feedData}
              advertoryWidescreenTargetId={advertoryWidescreenTargetId}
              isLoggedIn={isLoggedIn}
              layoutType={feedLayoutType}
              endOfFeed={noMoreArticles}
              headerTitle={headerTitle || ""}
            />
          )}
        </>
      )}

      {noMoreArticles ? (
        <div className={styles.endOfFeed} />
      ) : (
        <>
          <div className={styles.loader}>{loading && <DotLoader />}</div>
          <div ref={paginationRef} className={styles.feedLoadLimit}></div>
        </>
      )}
    </div>
  );
};

export { FeedLoader };
